import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles } from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";
import useSeoHead from "../useSeoHead";
import { CompanyDirectory } from "../components/CompanyDirectory";

export default function Directory() {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [capability, setCapability] = useState("");
  const [certification, setCertification] = useState("");
  const [industry, setIndustry] = useState("");
  const [city, setCity] = useState("");
  const [profileStatus, setProfileStatus] = useState("");
  const [immexOnly, setImmexOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allCaps, setAllCaps] = useState([]);
  const [showAllCaps, setShowAllCaps] = useState(false);

  /* Brief SEO V2 · #1 + #3 — Directory SEO head + ItemList schema */
  useSeoHead({
    title:
      lang === "es"
        ? "Directorio de Fabricantes IMMEX · Chihuahua–El Paso | Corridor Digital"
        : "IMMEX Manufacturer Directory · Chihuahua–El Paso | Corridor Digital",
    description:
      lang === "es"
        ? "Directorio de fabricantes IMMEX en el corredor Chihuahua–El Paso (Juárez, Parral, Chihuahua City). Basado en el listado oficial IMMEX y evidencia de investigación citada. Filtra por capacidad, certificación, parque industrial y ciudad. RFQ directo."
        : "Directory of IMMEX manufacturers on the Chihuahua–El Paso corridor (Juárez, Parral, Chihuahua City). Based on the official IMMEX listing and cited research evidence. Filter by capability, certification, industrial park, and city. Direct RFQ.",
    path: "/directory",
    lang,
    jsonLd: items && items.length > 0
      ? [
          {
            id: "ld-directory-itemlist",
            payload: {
              "@context": "https://schema.org",
              "@type": "ItemList",
              numberOfItems: items.length,
              itemListElement: items.slice(0, 30).map((m, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://corridordigital.net/m/${m.slug}`,
                name: m.display_name || m.company_name || m.legal_entity || m.slug,
              })),
            },
          },
        ]
      : [],
  });

  async function load() {
    setLoading(true);
    try {
      // iter48 — bumped to limit:2000 so every published plant reaches the
      // browser (production has 1,078 published, previously capped at 500).
      const params = { limit: 2000 };
      if (q) params.q = q;
      if (capability) params.capability = capability;
      if (certification) params.certification = certification;
      if (industry) params.industry = industry;
      if (city) params.city = city;
      if (profileStatus) params.profile_status = profileStatus;
      if (immexOnly) params.immex_certified = true;
      if (verifiedOnly) params.verified = true;
      const { data } = await api.get("/manufacturers", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    (async () => {
      await load();
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/capabilities");
        setAllCaps(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  return (
    <div data-testid="directory-page">
      {/* iter-relaunch banner — remove after July 2026 relaunch */}
      <div
        className="w-full"
        style={{
          background: "var(--ink)",
          color: "#fff",
          borderBottom: "1px solid var(--ink)",
        }}
        data-testid="directory-relaunch-banner"
      >
        <div className="container-cd py-3 flex items-start md:items-center gap-3 flex-wrap">
          <span
            className="font-mono text-[10px] px-2 py-0.5"
            style={{
              background: "var(--orange)",
              color: "#fff",
              letterSpacing: "0.08em",
              flexShrink: 0,
            }}
          >
            {lang === "es" ? "RELANZAMIENTO" : "RELAUNCHING"}
          </span>
          <div className="font-mono text-xs md:text-sm" style={{ letterSpacing: "0.01em" }}>
            {lang === "es"
              ? "Directorio en relanzamiento con datos verificados del registro IMMEX — julio 2026."
              : "Directory relaunching with verified IMMEX registry data — July 2026."}
          </div>
        </div>
      </div>
      <section className="container-cd pt-16 pb-8" style={{ borderBottom: "1px solid var(--line)" }}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline">CORRIDOR · MFG · {items.length} {t("dir.results")}</div>
            <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9] max-w-4xl">
              {t("dir.title")}
            </h1>
            <div
              className="mt-4 inline-flex items-center gap-2 cd-ai-badge"
              title={
                lang === "es"
                  ? "Resultados del directorio aparecen en respuestas de ChatGPT, Perplexity y Copilot"
                  : "Directory results appear in ChatGPT, Perplexity and Copilot answers"
              }
              data-testid="directory-ai-citation-badge"
            >
              <span className="cd-ai-badge__dot" />
              <Sparkles size={11} />
              {lang === "es" ? "Citado por ChatGPT" : "Appearing in AI search results"}
            </div>
          </div>
          <Link to="/digest" className="btn btn-ghost btn-sm" data-testid="directory-digest-link">
            {lang === "es" ? "Ver Corridor Digest →" : "View Corridor Digest →"}
          </Link>
        </div>
        {allCaps.length > 0 && (
          <div className="mt-8" data-testid="directory-capability-rail">
            <div className="overline mb-3">
              {lang === "es" ? "EXPLORAR POR CAPACIDAD" : "BROWSE BY CAPABILITY"}
            </div>
            <div className="flex flex-wrap gap-2">
              {(showAllCaps ? allCaps : allCaps.slice(0, 12)).map((c) => (
                <Link
                  key={c.slug}
                  to={`/c/${c.slug}`}
                  className="cd-chip cd-chip-interactive font-mono text-xs"
                  data-testid={`directory-cap-${c.slug}`}
                >
                  {c.name} · {c.count}
                </Link>
              ))}
              {allCaps.length > 12 && (
                <button
                  onClick={() => setShowAllCaps((v) => !v)}
                  className="cd-chip cd-chip-interactive font-mono text-xs"
                  style={{ cursor: "pointer", borderStyle: "dashed" }}
                  data-testid="directory-cap-toggle-all"
                >
                  {showAllCaps
                    ? lang === "es" ? "Mostrar menos" : "Show fewer"
                    : lang === "es"
                      ? `Ver las ${allCaps.length} categorías →`
                      : `Show all ${allCaps.length} categories →`}
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <div className="container-cd py-10 grid md:grid-cols-12 gap-10">
        {/* Filters */}
        <aside className="md:col-span-3" data-testid="directory-filters">
          <div className="space-y-6">
            <FilterInput label={t("dir.search")} value={q} onChange={setQ} testid="filter-q" icon={Search} />
            <FilterInput label={t("dir.filter.capability")} value={capability} onChange={setCapability} testid="filter-capability" />
            <FilterInput label={t("dir.filter.certification")} value={certification} onChange={setCertification} testid="filter-certification" />
            <FilterInput label={t("dir.filter.industry")} value={industry} onChange={setIndustry} testid="filter-industry" />
            <FilterInput label={t("dir.filter.city")} value={city} onChange={setCity} testid="filter-city" />

            {/* New filter — profile status */}
            <div>
              <label className="overline">
                {lang === "es" ? "ESTADO DE PERFIL" : "PROFILE STATUS"}
              </label>
              <select
                value={profileStatus}
                onChange={(e) => setProfileStatus(e.target.value)}
                className="cd-input mt-2 font-mono text-xs"
                data-testid="filter-profile-status"
              >
                <option value="">{lang === "es" ? "Todos" : "All"}</option>
                <option value="verified_founding_member">
                  {lang === "es" ? "Miembro Fundador" : "Founding Member"}
                </option>
                <option value="claimed">{lang === "es" ? "Reclamado" : "Claimed"}</option>
                <option value="unclaimed">{lang === "es" ? "Sin reclamar" : "Unclaimed"}</option>
              </select>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={immexOnly}
                onChange={(e) => setImmexOnly(e.target.checked)}
                data-testid="filter-immex-certified"
              />
              <span className="font-mono text-xs uppercase tracking-wider">
                {lang === "es" ? "Solo certificados IMMEX" : "IMMEX certified only"}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
                data-testid="filter-verified"
              />
              <span className="font-mono text-xs uppercase tracking-wider">{t("dir.verified_only")}</span>
            </label>
            <button className="cd-cta-btn-primary w-full" onClick={load} data-testid="filter-apply-btn">
              {t("common.translate") /* using as generic verb container */ ? "APPLY" : "APPLY"}
            </button>
          </div>
        </aside>

        {/* Results */}
        <main className="md:col-span-9">
          {loading ? (
            <div className="font-mono text-sm overline">{t("common.loading")}</div>
          ) : items.length === 0 ? (
            <div className="cd-card p-12 text-center font-mono text-sm" data-testid="directory-empty">
              {t("dir.empty")}
            </div>
          ) : (
            <CompanyDirectory items={items} lang={lang} />
          )}
        </main>
      </div>
    </div>
  );
}

function FilterInput({ label, value, onChange, testid, icon: Icon }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <div className="mt-2 flex items-center gap-2">
        {Icon ? <Icon size={14} style={{ color: "var(--ink-2)" }} /> : null}
        <input
          className="cd-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      </div>
    </div>
  );
}
